/**
 * 29 - Eliminate the General (10 §2): a walled compound on inspection day. The general walks his round of
 * it with his bodyguards in file behind him; his staff car waits on the hardstanding. Put him out of the
 * fight. The quiet is strict until the shot: the moment the garrison is alarmed (alert 2) he is hurried
 * away, and thirty seconds later he is running for the north edge - gone when he gets there.
 *
 * ROUND4 §1: he really leaves by car. At alert 2 the staff car on the hardstanding starts up - the parked prop
 * is replaced by a `staffCar` under way - and it WAITS while the general runs to it. He gets in, it drives out
 * of the gate and down the road, and when it is off the end of its run he is gone. Put a LAW or a charge into
 * the car while he is in it and the job is done.
 */

import type { EnemyKind } from '../../sim/types';
import { FLAG } from '../grid';
import type { Point } from '../types';
import { checker, clutter, countKind, guardAtDoor, guardsAbout, insideRect, required, ruleOf, structure, targetsOf } from './helpers';
import { RECALL } from './helpers3b';
import { escortedRound, type RoundMan } from './helpers3bRound';
import { roadRun, vehicle, vehiclesOf } from './helpers4Vehicles';
import type { ArchetypeSpec } from './types';

const GONE_AFTER = 30;
/** The tag the hurrying-away wakes, the car's pace, and how far down the road it is away. */
const LEAVES = 'targetFlees';
const CAR_SPEED = 7;
const RUN_OUT = 30;
/** The hardstanding stands this near the gate, and its lane out is kept this wide. */
const CAR_NEAR = 14;
const LANE_HALF = 2.6;
/** His round keeps this far inside the wall: room for the file, and clear of the goal's own perimeter patrol. */
const ROUND_INSET = 7;

/** Two bodyguards at threat 1, five at threat 9. */
export const bodyguardsFor = (threat: number): number => 2 + Math.floor(threat / 3);

export const eliminateGeneral: ArchetypeSpec = {
  id: 'eliminate-general',
  name: 'ELIMINATE THE GENERAL',
  nativeBiome: 'desert',
  baseThreat: 6,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'wall',
  sealed: false,
  layout: 'compound',
  size: { w: [52, 60], d: [30, 34] },
  river: false,
  patrolLoop: 'inside',
  breach: { min: 0, max: 0 },

  build(g) {
    // What he has come to inspect: huts and a guard house round the edge, the middle left open for the parade.
    // R4: the car stands within reach of the gate, and the lane between the two is reserved before anything
    // else is built - a staff car cannot drive through a hut, and nothing may be put in its way.
    const placed: Point[] = [];
    const gate = g.ctx.plan.roadEnd;
    const apron = {
      x0: Math.max(g.inner.x0, gate.x - CAR_NEAR), x1: Math.min(g.inner.x1, gate.x + CAR_NEAR),
      z0: Math.max(g.inner.z0, g.rect.z1 - CAR_NEAR), z1: g.inner.z1,
    };
    const car = required(g, 'staffCar', { target: true, from: placed, minDist: 0, margin: 1.4, within: apron });
    const lane: Point[] = [{ x: car.x, z: car.z + 3 }, { x: gate.x, z: g.rect.z1 - 5 }];
    g.map.mask.line(lane.map((p): [number, number] => [p.x, p.z]), LANE_HALF, FLAG.keep);
    const house = required(g, 'guardHouse', { minDist: 9, from: placed, margin: 1.4 });
    for (let i = 0; i < 3; i++) structure(g, i === 0 ? 'hut' : g.kit.shelter, { minDist: 8, from: placed, margin: 1.4 });
    structure(g, 'alarmPost', { margin: 1 });
    clutter(g, 'crate', 3);
    guardAtDoor(g, house);
    guardsAbout(g, 1 + Math.floor(g.threat / 4));

    const i = g.inner;
    const d = Math.min(ROUND_INSET, (i.x1 - i.x0) / 4, (i.z1 - i.z0) / 4);
    const corners: Point[] = [
      { x: i.x0 + d, z: i.z1 - d }, { x: i.x1 - d, z: i.z1 - d }, { x: i.x1 - d, z: i.z0 + d }, { x: i.x0 + d, z: i.z0 + d },
    ];
    const start = g.rng.int(4);
    const round = corners.map((_, n) => corners[(n + start) % 4] ?? { x: i.x0 + d, z: i.z0 + d });
    const guards: EnemyKind = 'elite';
    const men: RoundMan[] = [{ kind: 'officer', extra: { objectiveTarget: true } }];
    for (let n = 0; n < bodyguardsFor(g.threat); n++) men.push({ kind: guards });
    escortedRound(g, round, men);

    // R4: the car that comes for him. Off the hardstanding, down the reserved lane, out of the gate, and away.
    const road = (g.map.roads[0]?.points ?? []).map(([rx, rz]) => ({ x: rx, z: rz }));
    const away = roadRun(road, g.rect.z1 - 3, Math.min(g.ctx.sizeZ - 6, g.rect.z1 + RUN_OUT));
    vehicle(g, {
      kind: 'staffCar', speed: CAR_SPEED, wakeTag: LEAVES, replacesPropId: car.id, from: { x: car.x, z: car.z },
      route: [...lane, ...away],
    });

    return {
      objective: {
        kind: 'killTarget', label: 'ELIMINATE THE GENERAL', targetPropIds: [car.id], minFraction: 1,
        targetName: 'THE GENERAL',
        rules: [
          {
            type: 'alertConsequence', level: 2, delaySeconds: GONE_AFTER, effect: 'targetFlees',
            orders: 'STRICT QUIET UNTIL THE SHOT: IF THE ALARM IS RAISED THE GENERAL IS HURRIED AWAY, AND GONE IN 30 SECONDS',
            warning: 'THE GENERAL IS BEING HURRIED AWAY — GONE IN',
            reason: 'THE GENERAL GOT AWAY',
          },
          RECALL,
        ],
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('eliminate-general');
    const o = def.objective;
    check(o.kind === 'killTarget' && o.targetName === 'THE GENERAL', 'objective must be killTarget, and name the general');
    const generals = def.spawns.filter((s) => s.objectiveTarget === true);
    const [general] = generals;
    check(generals.length === 1 && general?.kind === 'officer', `needs exactly one general, has ${generals.length}`);
    check(general?.dormantUntil === undefined, 'the general is there from the start: he is inspecting the place');
    if (general) {
      check(insideRect(def.goalZone, general.x, general.z), 'the general must be inside the compound');
      check((general.patrol?.length ?? 0) >= 4, 'the general must walk his round of inspection');
      const file = def.spawns.filter((s) => s !== general && s.kind === 'elite' && s.dormantUntil === undefined
        && s.patrol?.length === general.patrol?.length
        && Math.abs(s.x - general.x) <= 6 && Math.abs(s.z - general.z) <= 8);
      check(file.length >= bodyguardsFor(def.threat), `he needs ${bodyguardsFor(def.threat)} bodyguards at threat ${def.threat}`);
    }
    const [car] = targetsOf(def, 'staffCar');
    check(car !== undefined && countKind(def, 'staffCar') === 1, 'his staff car must stand in the compound, and mark it on the map');
    if (car) check(probe.breach(car.id) === 0 && car.target === true, 'the compound must be reachable through its gates');
    const flees = ruleOf(def, 'alertConsequence');
    check(
      flees?.effect === 'targetFlees' && flees.level === 2 && flees.delaySeconds === GONE_AFTER,
      `alert 2 must see him gone after ${GONE_AFTER} s`,
    );
    check(
      flees?.orders !== undefined && flees.warning !== undefined && flees.reason !== undefined,
      'the rule must carry its orders, its countdown and its reason',
    );
    // R4: "hurried to his car" is a car. It is the parked one, it waits on the alarm, and it drives out.
    const cars = vehiclesOf(def.spawns);
    const [staff] = cars;
    check(cars.length === 1 && staff?.kind === 'staffCar', 'the general must have one staff car to be hurried into');
    check(
      staff?.dormantUntil === 'timedEvent' && staff.wakeTag === LEAVES && staff.replacesPropId === car?.id,
      'the car that leaves must be the car that was parked, and only once the alarm is up',
    );
    const run = staff?.route?.points.points ?? [];
    const exit = run[run.length - 1];
    check(staff?.route?.loop === false && exit !== undefined && exit[1] > def.goalZone.z1,
      'the staff car must drive out of the compound and off the end of its run');
    return out;
  },
};
